import {useMemo} from 'react';
import {paths} from 'packages/common/routes/paths';
import {Icon} from '@iconify/react';
import {useUserDetailsContext} from 'packages/common/hooks';
import {roles} from 'packages/common/utils/rolesConfig';

/**
 * Generates a navigation data structure based on the user's company details, onboarding status, role, and feature availability.
 *
 * @returns {Array} An array of menu sections, each containing a subheader and an array of items with titles, paths, and icons for navigation purposes.
 */

export function useNavData() {
  const {userDetails} = useUserDetailsContext();
  const currentCompany = userDetails?.companyDetails?.[0] || null;
  const isOnboardingCompleted = currentCompany?.onboardingCompleted;
  const userRole = userDetails?.role;

  const features = useMemo(() =>
    currentCompany?.features || {},
  [currentCompany],
  );

  const data = useMemo(() => {
    const baseMenu = [];

    if (!isOnboardingCompleted) {
      baseMenu.push({
        subheader: 'Bienvenue',
        items: [
          {
            title: 'Configuration initiale',
            path: paths.onboarding.root,
            icon: <Icon icon="mdi:progress-check" height="24" />,
          },
        ],
      });
    }

    if (isOnboardingCompleted) {
      baseMenu.push({
        items: [
          {
            title: 'Dashboard',
            path: paths.app.dashboard,
            icon: <Icon icon="solar:widget-5-linear" height="24" />,
          },
          ...(userRole === roles.COMPANY_ADMIN ? [{
            title: 'Rapports',
            path: paths.app.reporting,
            icon: <Icon icon="solar:presentation-graph-linear" height="24" />,
          }] : []),
          ...(userRole === roles.COMPANY_ADMIN && features.customers ? [{
            title: 'Clients',
            path: paths.app.customers.list,
            icon: <Icon icon="ph:users-three" height="24" />,
          }] : []),
        ],
      });

      if (userRole === roles.COMPANY_ADMIN) {
        baseMenu.push({
          subheader: 'Bons cadeaux',
          items: [
            {
              title: 'Catalogue',
              path: paths.app.giftVoucher.catalog.list,
              icon: <Icon icon="solar:clipboard-list-linear" height="24" />,
            },
            {
              title: 'Tous mes bons',
              path: paths.app.giftVoucher.manage,
              icon: <Icon icon="solar:gift-outline" height="24" />,
            },
            {
              title: 'Transactions',
              path: paths.app.giftVoucher.transactions,
              icon: <Icon icon="uil:transaction" height="24" />,
            },
            {
              title: 'Recherche',
              path: paths.app.giftVoucher.search,
              icon: <Icon icon="solar:bill-check-linear" height="24" />,
            },
          ],
        });
      } else {
        baseMenu.push({
          subheader: 'Bons cadeaux',
          items: [
            {
              title: 'Recherche',
              path: paths.app.giftVoucher.search,
              icon: <Icon icon="solar:bill-check-linear" height="24" />,
            },
          ],
        });
      }

      if (userRole === roles.COMPANY_ADMIN) {
        if (features.wellness) {
          baseMenu.push({
            subheader: 'Wellness',
            items: [
              {
                title: 'Calendrier',
                path: paths.app.wellness.calendar.root,
                icon: <Icon icon="carbon:calendar" height="24" />,
              },
              {
                title: 'Réservations',
                path: paths.app.wellness.bookings.root,
                icon: <Icon icon="solar:calendar-mark-linear" height="24" />,
              },
              {
                title: 'Caisse',
                path: paths.app.wellness.pos.sale,
                icon: <Icon icon="solar:hand-money-outline" height="24" />,
              },
              {
                title: 'Espaces',
                path: paths.app.wellness.spaces.list,
                icon: <Icon icon="material-symbols:spa-outline" height="24" />,
              },
              {
                title: 'Catalogue',
                path: paths.app.wellness.catalog.root,
                icon: <Icon icon="solar:book-bookmark-minimalistic-outline" height="24" />,
              },
              {
                title: 'Employés',
                path: paths.app.employees.list,
                icon: <Icon icon="solar:users-group-rounded-linear" height="24" />,
              },
              {
                title: 'Configuration',
                path: paths.app.wellness.config.root,
                icon: <Icon icon="solar:settings-linear" height="24" />,
              },
            ],
          });
        }
      }

      if (features.restaurant) {
        baseMenu.push({
          subheader: 'Restaurant',
          items: [
            {
              title: 'Réservations',
              path: paths.app.restaurant.reservations,
              icon: <Icon icon="solar:calendar-mark-linear" height="24" />,
            },
            {
              title: 'Plan de salle',
              path: paths.app.restaurant.seating,
              icon: <Icon icon="solar:chair-linear" height="24" />,
            },
            {
              title: 'Services',
              path: paths.app.restaurant.services,
              icon: <Icon icon="solar:document-add-outline" height="24" />,
            },
          ],
        });
      }

      baseMenu.push({
        subheader: 'Administration',
        items: [
          {
            title: 'Mon établissement',
            path: paths.app.company,
            icon: <Icon icon="solar:buildings-3-linear" height="24" />,
          },
          {
            title: 'Administration',
            path: paths.app.settings,
            icon: <Icon icon="solar:settings-linear" height="24" />,
          },
          {
            title: 'Widgets',
            path: paths.app.widgets,
            icon: <Icon icon="solar:code-square-linear" height="22" />,
          },
        ],
      });
    }

    return baseMenu;
  }, [isOnboardingCompleted, userRole, features]);

  return data;
}
